import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.6-6.1c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4 0-.5.1-.7l.4-.5c.1-.2.2-.3.3-.5v-.5L9.3 7.7c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-.9.9-.9 2.2s.9 2.5 1.1 2.7c.1.2 1.8 2.8 4.4 3.9.6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
    <path d="M18.9 2H22l-6.8 7.8L23 22h-6.3l-4.9-6.4L6.2 22H3l7.3-8.3L2.5 2h6.4l4.4 5.9L18.9 2zm-1.1 18h1.7L7.4 3.9H5.6L17.8 20z" />
  </svg>
);

export default function ShareButtons({ title, className = "" }) {
  const [open, setOpen] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `${title} — doe via Pix e ajude! ${url}`;

  const targets = [
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(text)}`, bg: "bg-[#25D366]", icon: <WhatsAppIcon /> },
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      bg: "bg-[#1877F2]",
      icon: <span className="font-display text-sm font-extrabold">f</span>,
    },
    {
      label: "X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      bg: "bg-black",
      icon: <XIcon />,
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado para compartilhar!");
    } catch {
      toast.error("Não foi possível copiar o link");
    }
  };

  return (
    <div className={className}>
      <Button
        type="button"
        data-testid="campaign-share-btn"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        className="h-12 w-full rounded-full border-2 border-[#EFE9DF] bg-white font-extrabold text-[#261C14] hover:border-[#FF6D47] hover:bg-white hover:text-[#FF6D47]"
      >
        <Share2 className="h-4 w-4" /> Compartilhar
      </Button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 380, damping: 26 }}
            className="mt-3 grid grid-cols-4 gap-2"
          >
            {targets.map((t) => (
              <a
                key={t.label}
                href={t.href}
                target="_blank"
                rel="noreferrer"
                aria-label={`Compartilhar no ${t.label}`}
                className={`${t.bg} grid h-11 place-items-center rounded-xl text-white transition hover:opacity-85`}
              >
                {t.icon}
              </a>
            ))}
            <button
              type="button"
              onClick={copy}
              aria-label="Copiar link"
              data-testid="share-copy-link-btn"
              className="grid h-11 place-items-center rounded-xl bg-[#F7F3ED] text-[#261C14] transition hover:bg-[#FFE2D9]"
            >
              <Link2 className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
